import { api, ApiError } from './client';
import type {
  Alert,
  Place,
  Company,
  CreateDriverAccountInput,
  DeviceCommand,
  DriverAccount,
  DriverAccountStatus,
  PaymentMethod,
  PaymentSummary,
  RecordTransactionInput,
  Transaction,
  TransactionStatus,
  AlertSeverity,
  AlertStatus,
  AlertSummary,
  AlertType,
  AuditLog,
  AuthTokens,
  CreateDeviceInput,
  CreateDocumentInput,
  CreateGeofenceInput,
  CreateRecordInput,
  CreateScheduleInput,
  CreateUserInput,
  CreateVehicleInput,
  CustomRole,
  Device,
  DeviceStatus,
  Geofence,
  Id,
  MaintenanceRecord,
  MaintenanceSchedule,
  OdometerReading,
  Paginated,
  PaginationQuery,
  Permission,
  Role,
  SessionUser,
  SimCard,
  SimConnectivitySummary,
  SimTrackerSim,
  SimSession,
  TelemetryStats,
  TrailPoint,
  Trip,
  TripStatus,
  UpdateUserInput,
  UpdateVehicleInput,
  User,
  UtilizationRow,
  Vehicle,
  VehicleDocument,
  VehicleStatus,
  CreateDriverInput,
  Driver,
  DriverPaymentFilter,
  UpdateDriverInput,
} from '@/types/api';

/* ── auth ─────────────────────────────────────────────────────────────────── */

export const authApi = {
  login: (body: { email: string; password: string; totp?: string }) =>
    api.post<AuthTokens>('/auth/login', body, { anonymous: true }),
  logout: () => api.post<void>('/auth/logout'),
  forgotPassword: (email: string) =>
    api.post<void>('/auth/forgot-password', { email }, { anonymous: true }),
  resetPassword: (body: { token: string; newPassword: string }) =>
    api.post<void>('/auth/reset-password', body, { anonymous: true }),
  me: () => api.get<SessionUser & User>('/users/me'),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.patch<void>('/users/me/password', body),
};

/* ── vehicles ─────────────────────────────────────────────────────────────── */

export interface VehicleQuery extends PaginationQuery {
  search?: string;
  status?: VehicleStatus;
  group?: string;
  assignedDriverId?: Id;
}

export const vehiclesApi = {
  list: (query?: VehicleQuery) => api.get<Paginated<Vehicle>>('/vehicles', query),
  live: () => api.get<Vehicle[]>('/vehicles/live'),
  nearby: (query: { lat: number; lng: number; radiusMeters?: number }) =>
    api.get<Vehicle[]>('/vehicles/nearby', query),
  get: (id: Id) => api.get<Vehicle>(`/vehicles/${id}`),
  create: (body: CreateVehicleInput) => api.post<Vehicle>('/vehicles', body),
  update: (id: Id, body: UpdateVehicleInput) => api.patch<Vehicle>(`/vehicles/${id}`, body),
  decommission: (id: Id) => api.patch<Vehicle>(`/vehicles/${id}/decommission`),
  adjustOdometer: (id: Id, body: { valueKm: number; reason: string }) =>
    api.post<Vehicle>(`/vehicles/${id}/odometer`, body),
  odometerHistory: (id: Id) => api.get<OdometerReading[]>(`/vehicles/${id}/odometer`),
};

/* ── devices ──────────────────────────────────────────────────────────────── */

/**
 * The API's QueryDevicesDto only accepts search and status, and it rejects
 * unknown query params outright — so filtering by vehicle happens client-side.
 */
export interface DeviceQuery extends PaginationQuery {
  search?: string;
  status?: DeviceStatus;
}

export interface SimQuery extends PaginationQuery {
  search?: string;
  session?: SimSession;
}

export const devicesApi = {
  list: (query?: DeviceQuery) => api.get<Paginated<Device>>('/devices', query),
  sims: (query?: SimQuery) => api.get<Paginated<SimCard>>('/devices/sims', query),
  get: (id: Id) => api.get<Device>(`/devices/${id}`),
  create: (body: CreateDeviceInput) => api.post<Device>('/devices', body),
  update: (id: Id, body: Partial<Omit<CreateDeviceInput, 'imei'>>) =>
    api.patch<Device>(`/devices/${id}`, body),
  assign: (id: Id, vehicleId: Id, note?: string) =>
    api.post<Device>(`/devices/${id}/assign`, { vehicleId, note }),
  unassign: (id: Id) => api.post<Device>(`/devices/${id}/unassign`),
  decommission: (id: Id) => api.patch<Device>(`/devices/${id}/decommission`),
};

/* ── telemetry & trips ────────────────────────────────────────────────────── */

export const telemetryApi = {
  latest: () => api.get<Vehicle[]>('/telemetry/latest'),
  trail: (vehicleId: Id, query: { from?: string; to?: string; limit?: number }) =>
    api.get<TrailPoint[]>(`/telemetry/vehicles/${vehicleId}/trail`, query),
  stats: (vehicleId: Id, query: { from: string; to: string }) =>
    api.get<TelemetryStats>(`/telemetry/vehicles/${vehicleId}/stats`, query),
};

export interface TripQuery extends PaginationQuery {
  vehicleId?: Id;
  driverId?: Id;
  status?: TripStatus;
  from?: string;
  to?: string;
}

export const tripsApi = {
  list: (query?: TripQuery) => api.get<Paginated<Trip>>('/trips', query),
  get: (id: Id) => api.get<Trip>(`/trips/${id}`),
  utilization: (query: { from: string; to: string }) =>
    api.get<UtilizationRow[]>('/trips/utilization', query),
};

/* ── geofences ────────────────────────────────────────────────────────────── */

export interface GeofenceQuery extends PaginationQuery {
  search?: string;
  shape?: 'POLYGON' | 'CIRCLE';
  isActive?: boolean;
  vehicleId?: Id;
}

export const geofencesApi = {
  list: (query?: GeofenceQuery) => api.get<Paginated<Geofence>>('/geofences', query),
  get: (id: Id) => api.get<Geofence>(`/geofences/${id}`),
  containing: (query: { lat: number; lng: number }) =>
    api.get<Geofence[]>('/geofences/containing', query),
  create: (body: CreateGeofenceInput) => api.post<Geofence>('/geofences', body),
  update: (id: Id, body: Partial<CreateGeofenceInput>) =>
    api.patch<Geofence>(`/geofences/${id}`, body),
  remove: (id: Id) => api.delete<void>(`/geofences/${id}`),
};

/* ── alerts ───────────────────────────────────────────────────────────────── */

export interface AlertQuery extends PaginationQuery {
  type?: AlertType;
  severity?: AlertSeverity;
  status?: AlertStatus;
  vehicleId?: Id;
  from?: string;
  to?: string;
}

export const alertsApi = {
  list: (query?: AlertQuery) => api.get<Paginated<Alert>>('/alerts', query),
  summary: () => api.get<AlertSummary>('/alerts/summary'),
  get: (id: Id) => api.get<Alert>(`/alerts/${id}`),
  acknowledge: (id: Id) => api.patch<Alert>(`/alerts/${id}/acknowledge`),
  resolve: (id: Id, note?: string) => api.patch<Alert>(`/alerts/${id}/resolve`, { note }),
};

/* ── users & roles ────────────────────────────────────────────────────────── */

export interface UserQuery extends PaginationQuery {
  search?: string;
  role?: Role;
  isActive?: boolean;
}

export const usersApi = {
  list: (query?: UserQuery) => api.get<Paginated<User>>('/users', query),
  get: (id: Id) => api.get<User>(`/users/${id}`),
  create: (body: CreateUserInput) => api.post<User>('/users', body),
  update: (id: Id, body: UpdateUserInput) => api.patch<User>(`/users/${id}`, body),
  deactivate: (id: Id) => api.patch<User>(`/users/${id}/deactivate`),
};

export const rolesApi = {
  list: (query?: PaginationQuery) => api.get<Paginated<CustomRole>>('/roles', query),
  get: (id: Id) => api.get<CustomRole>(`/roles/${id}`),
  create: (body: { name: string; description?: string; permissions: Permission[] }) =>
    api.post<CustomRole>('/roles', body),
  update: (id: Id, body: Partial<{ name: string; description: string; permissions: Permission[] }>) =>
    api.patch<CustomRole>(`/roles/${id}`, body),
  remove: (id: Id) => api.delete<void>(`/roles/${id}`),
};

/* ── maintenance ──────────────────────────────────────────────────────────── */

export const maintenanceApi = {
  schedules: (query?: PaginationQuery & { vehicleId?: Id }) =>
    api.get<Paginated<MaintenanceSchedule>>('/maintenance/schedules', query),
  createSchedule: (body: CreateScheduleInput) =>
    api.post<MaintenanceSchedule>('/maintenance/schedules', body),
  updateSchedule: (id: Id, body: Partial<CreateScheduleInput>) =>
    api.patch<MaintenanceSchedule>(`/maintenance/schedules/${id}`, body),
  removeSchedule: (id: Id) => api.delete<void>(`/maintenance/schedules/${id}`),

  records: (query?: PaginationQuery & { vehicleId?: Id }) =>
    api.get<Paginated<MaintenanceRecord>>('/maintenance/records', query),
  createRecord: (body: CreateRecordInput) => api.post<MaintenanceRecord>('/maintenance/records', body),

  documents: (query?: PaginationQuery & { vehicleId?: Id }) =>
    api.get<Paginated<VehicleDocument>>('/maintenance/documents', query),
  createDocument: (body: CreateDocumentInput) =>
    api.post<VehicleDocument>('/maintenance/documents', body),
  updateDocument: (id: Id, body: Partial<CreateDocumentInput>) =>
    api.patch<VehicleDocument>(`/maintenance/documents/${id}`, body),
  removeDocument: (id: Id) => api.delete<void>(`/maintenance/documents/${id}`),
};

/* ── payments ─────────────────────────────────────────────────────────────── */

export interface DriverAccountQuery extends PaginationQuery {
  search?: string;
  status?: DriverAccountStatus;
  unpaidOn?: string;
}

export interface TransactionQuery extends PaginationQuery {
  status?: TransactionStatus;
  method?: PaymentMethod;
  driverId?: Id;
  vehicleId?: Id;
  from?: string;
  to?: string;
}

export const paymentsApi = {
  summary: () => api.get<PaymentSummary>('/payments/summary'),
  accounts: (query?: DriverAccountQuery) =>
    api.get<Paginated<DriverAccount>>('/payments/accounts', query),
  account: (id: Id) => api.get<DriverAccount>(`/payments/accounts/${id}`),
  createAccount: (body: CreateDriverAccountInput) =>
    api.post<DriverAccount>('/payments/accounts', body),
  updateAccount: (id: Id, body: Partial<CreateDriverAccountInput>) =>
    api.patch<DriverAccount>(`/payments/accounts/${id}`, body),
  transactions: (query?: TransactionQuery) =>
    api.get<Paginated<Transaction>>('/payments/transactions', query),
  record: (body: RecordTransactionInput) =>
    api.post<Transaction>('/payments/transactions', body),
};

/* ── drivers ──────────────────────────────────────────────────────────────── */

export interface DriverQuery extends PaginationQuery {
  search?: string;
  payment?: DriverPaymentFilter;
  isActive?: boolean;
}

export const driversApi = {
  list: (query?: DriverQuery) => api.get<Paginated<Driver>>('/drivers', query),
  get: (id: Id) => api.get<Driver>(`/drivers/${id}`),
  create: (body: CreateDriverInput) => api.post<Driver>('/drivers', body),
  update: (id: Id, body: UpdateDriverInput) => api.patch<Driver>(`/drivers/${id}`, body),
};

/* ── remote immobilisation ────────────────────────────────────────────────── */

export const commandsApi = {
  list: (query?: PaginationQuery & { vehicleId?: Id }) =>
    api.get<Paginated<DeviceCommand>>('/commands', query),
  immobilise: (vehicleId: Id, reason: string) =>
    api.post<DeviceCommand>(`/commands/vehicles/${vehicleId}/immobilise`, { reason }),
  release: (vehicleId: Id, reason: string) =>
    api.post<DeviceCommand>(`/commands/vehicles/${vehicleId}/release`, { reason }),
};

/* ── audit ────────────────────────────────────────────────────────────────── */

export interface AuditQuery extends PaginationQuery {
  actorId?: Id;
  action?: string;
  entityType?: string;
  from?: string;
  to?: string;
}

export const auditApi = {
  list: (query?: AuditQuery) => api.get<Paginated<AuditLog>>('/audit-logs', query),
  forEntity: (entityType: string, entityId: string) =>
    api.get<AuditLog[]>(`/audit-logs/entity/${entityType}/${entityId}`),
};

/* ── companies (platform owner only) ──────────────────────────────────────── */

export const companiesApi = {
  list: (query?: PaginationQuery) => api.get<Paginated<Company>>('/companies', query),
  get: (id: Id) => api.get<Company>(`/companies/${id}`),
};

/* ── geocoding ────────────────────────────────────────────────────────────── */

export const geocodingApi = {
  /** Cached server-side, so repeat lookups of the same spot are nearly free. */
  reverse: (lat: number, lng: number) =>
    api.get<{ lat: number; lng: number; place: Place | null }>('/geocode/reverse', { lat, lng }),
};

/* ── SIM tracker (external) ───────────────────────────────────────────────── */

/**
 * A separate service from the fleet API, reached through the same-origin proxy
 * (Caddy in production, Vite in dev): it is plain HTTP and sends no CORS
 * headers, so the browser could not call it directly. It takes no auth and
 * knows nothing of companies, so it never goes through `api`.
 */
export const SIM_TRACKER_BASE = import.meta.env.VITE_SIM_TRACKER_BASE_URL ?? '/sim-tracker/api';

const simTrackerGet = async <T>(path: string): Promise<T> => {
  const res = await fetch(`${SIM_TRACKER_BASE}/${path}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new ApiError(res.status, 'SIM tracker is unavailable');
  return (await res.json()) as T;
};

export const simTrackerApi = {
  /** Every SIM the tracker watches, with its network status. */
  sims: () => simTrackerGet<SimTrackerSim[]>('get-sim-connectivity-sim-control.php'),
  connectivitySummary: async () => {
    const res = await fetch(
      `${SIM_TRACKER_BASE}/get-sim-connectivity-sim-control-summary-dashboard.php`,
      { headers: { Accept: 'application/json' } },
    );
    if (!res.ok) throw new ApiError(res.status, 'SIM tracker is unavailable');
    // Served as text/html by the PHP host, so parse regardless of content type.
    return (await res.json()) as SimConnectivitySummary;
  },
};
